<template>
  <div class="w-full h-full p-1 pt-0 bg-gray-200/20 rounded-xl">
    <div class="mx-4 pt-3.5 pb-[13px] border-b border-gray-200/50">
      <div class="flex items-center gap-x-2.5">
        <Icon :name="stemIcon" :size="22" class="stroke-gray-800" />

        <div
          class="font-secondary font-medium text-[15px] leading-[20px] text-gray-800"
        >
          {{ props.name }}
        </div>
      </div>
    </div>

    <div class="px-4 pt-4 pb-4">
      <div class="text-[12px] text-gray-500 leading-4 mb-[5px] font-primary">
        Isolated stem
      </div>

      <div class="flex items-center gap-x-5">
        <div
          class="min-w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center cursor-pointer"
          @click="handleClick"
        >
          <Icon :name="icon" :size="18" :style="iconStyles" />
        </div>

        <div ref="el" class="w-full"></div>
      </div>
    </div>

    <div v-if="props.sheetMusic" class="bg-white rounded-xl px-4 pt-2.5 pb-2.5">
      <div class="text-[12px] text-gray-500 leading-4 font-primary">
        Stem sheet music
      </div>

      <div class="flex items-center gap-x-5">
        <div class="relative min-w-0 flex-1 overflow-hidden">
          <div ref="sheetMusicRef" class="w-full"></div>
          <div
            class="absolute top-0 right-0 h-full w-32 bg-gradient-to-r from-transparent to-white pointer-events-none"
          ></div>
        </div>

        <UiButton icon="File" class="shrink-0" @click="handleViewMore">
          View
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Icon, type IconName, UiButton } from '@dacr/ui'
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps<{
  name: string
  url: string
  sheetMusic?: string
  trackIndex: number
}>()

const route = useRoute()
const router = useRouter()

const el = ref<HTMLElement>()
const wavesurfer = ref<InstanceType<typeof import('wavesurfer.js').default>>()
const sheetMusicRef = ref<HTMLElement>()

const icon = ref<IconName>('Play')
const sheetMusicPreviewZoom = 0.7

const iconStyles = computed(() => ({
  marginLeft: icon.value === 'Play' ? '3px' : null,
}))

const stemIcon = computed<IconName>(() => {
  const name = props.name.toLowerCase()
  if (name === 'vocals') return 'Mic'
  if (name === 'drums') return 'Drums'
  if (name === 'guitar') return 'Guitar'
  if (name === 'bass') return 'Bass'
  return 'Audio'
})

const handleClick = () => {
  wavesurfer.value?.playPause()
  icon.value = wavesurfer.value?.isPlaying() ? 'Pause' : 'Play'
}

const handleViewMore = () => {
  const base = `/file/${route.params.id}`
  router.push(
    `${base}/tracks/${props.trackIndex}/${props.name.toLowerCase()}/sheet-music`
  )
}

onMounted(async () => {
  if (!el.value) return

  const WaveSurfer = (await import('wavesurfer.js')).default

  wavesurfer.value = WaveSurfer.create({
    container: el.value,
    waveColor: '#AFB5C0',
    progressColor: '#222',
    url: props.url,
    height: 40,
    cursorWidth: 0,
  })

  if (props.sheetMusic && sheetMusicRef.value) {
    const musicSheet = new OpenSheetMusicDisplay(sheetMusicRef.value, {
      drawTitle: false,
      drawPartNames: false,
      drawCredits: false,
      drawingParameters: 'compacttight',
      drawMetronomeMarks: false,
      renderSingleHorizontalStaffline: true,
      autoResize: false,
    })
    await musicSheet.load(props.sheetMusic)
    musicSheet.Zoom = sheetMusicPreviewZoom
    musicSheet.render()
  }
})
</script>
